/**
 * Team/Group Service
 * Handles all API calls related to teams/groups
 */

import { supabase } from "../utils/supabaseClient";

export interface Team {
  id: string;
  name: string;
  description?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  organisation_id?: string;
  entities_ids?: string[];
}

export interface TeamMember {
  id: string;
  group_id: string;
  user_id: string;
  role: "admin" | "member";
  created_at: string;
  full_name?: string;
  email?: string;
  avatar_url?: string;
}

export interface TeamInvitation {
  id: string;
  group_id: string;
  email: string;
  status: "pending" | "accepted" | "rejected";
  created_at: string;
  updated_at: string;
  team_name?: string;
  organization_name?: string;
}

export interface TeamWithDetails extends Team {
  role: string;
  member_count: number;
  organization_name?: string;
}

/**
 * Create a new team/group
 */
export const createTeam = async (
  name: string,
  description?: string,
  orgId?: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .rpc("create_group", {
        group_name: name,
        group_description: description || null,
        org_id: orgId || null,
      });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error creating team:", error);
    throw error;
  }
};

/**
 * Get all teams for the current user
 */
export const getUserTeams = async (): Promise<TeamWithDetails[]> => {
  try {
    const { data, error } = await supabase
      .rpc("get_user_teams");

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching user teams:", error);
    throw error;
  }
};

/**
 * Get a single team by ID
 */
export const getTeam = async (id: string): Promise<Team | null> => {
  try {
    console.log(`[TeamService] Fetching team: ${id}`);

    // Try to get from user teams first (more reliable with RLS)
    try {
      const { data: userTeams, error: rpcError } = await supabase
        .rpc("get_user_teams");

      if (!rpcError && userTeams) {
        const team = userTeams.find((t: any) => t.id === id);
        if (team) {
          console.log(`[TeamService] ✅ Found team via RPC: ${team.name}`);
          return team;
        }
      }
    } catch (rpcError) {
      console.warn(`[TeamService] RPC failed for team ${id}:`, rpcError);
    }

    // Fallback to direct table access
    console.log(`[TeamService] ⚠️ Falling back to direct access for team ${id}`);
    const { data, error } = await supabase
      .from("base_groups")
      .select("*")
      .eq("id", id)
      .single();

    if (error) {
      console.error(`[TeamService] ❌ Direct access failed for team ${id}:`, error);
      return null;
    }

    console.log(`[TeamService] ✅ Found team via direct access: ${data?.name}`);
    return data;
  } catch (error) {
    console.error(`Error fetching team with ID ${id}:`, error);
    return null;
  }
};

/**
 * Get team members
 */
export const getTeamMembers = async (teamId: string): Promise<TeamMember[]> => {
  try {
    // First get the team members
    const { data: membersData, error: membersError } = await supabase
      .from("base_group_members")
      .select("*")
      .eq("group_id", teamId);

    if (membersError) throw membersError;

    if (!membersData || membersData.length === 0) {
      return [];
    }

    // Get user profiles for all members using RPC calls to avoid RLS issues
    const userIds = membersData.map((member) => member.user_id);

    let profilesData: any[] = [];
    try {
      console.log(`Fetching profiles for ${userIds.length} team members...`);

      // Use Promise.allSettled to get as many profiles as possible, even if some fail
      const profilePromises = userIds.map(async (userId) => {
        try {
          const { data, error } = await supabase
            .rpc("get_user_profile", { user_id: userId });

          if (!error && data && data.length > 0) {
            const profile = data[0]; // RPC returns array
            return {
              id: userId,
              full_name: profile.full_name || profile.username,
              email: profile.email
            };
          }
          return null;
        } catch (error) {
          console.warn(`Failed to get profile for team member ${userId}:`, error);
          return null;
        }
      });

      const profileResults = await Promise.allSettled(profilePromises);
      profilesData = profileResults
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => (result as PromiseFulfilledResult<any>).value);

      console.log(`Successfully fetched ${profilesData.length} out of ${userIds.length} team member profiles`);

    } catch (error) {
      console.error("Error fetching team member profiles:", error);
      // Continue without profile data
      profilesData = [];
    }

    // Combine the data
    return membersData.map((member: any) => {
      const profile = profilesData?.find((p) => p.id === member.user_id);
      return {
        id: member.id,
        group_id: member.group_id,
        user_id: member.user_id,
        role: member.role,
        created_at: member.created_at,
        full_name: profile?.full_name || `User ${member.user_id.slice(0, 8)}`,
        email: profile?.email || "No email available",
        avatar_url: undefined, // Set to undefined since column doesn't exist
      };
    });
  } catch (error) {
    console.error(`Error fetching members for team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Check if the current user is a team admin
 */
export const isTeamAdmin = async (teamId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .rpc("is_group_admin", {
        p_group_id: teamId,
      });

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error(`Error checking admin status for team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Invite a user to a team
 */
export const inviteToTeam = async (
  teamId: string,
  email: string
): Promise<TeamInvitation | null> => {
  try {
    const { data, error } = await supabase
      .rpc("invite_to_team", {
        team_id: teamId,
        user_email: email,
        user_role: "member",
      });

    if (error) throw error;

    // Fetch the invitation details
    if (data) {
      const { data: invitationData, error: invitationError } = await supabase
        .from("base_group_invitations")
        .select("*")
        .eq("id", data)
        .single();

      if (invitationError) throw invitationError;
      return invitationData;
    }

    return null;
  } catch (error) {
    console.error("Error inviting user to team:", error);
    throw error;
  }
};

/**
 * Accept a team invitation
 */
export const acceptTeamInvitation = async (
  invitationId: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .rpc("accept_team_invitation", {
        p_invitation_id: invitationId,
      });

    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error(`Error accepting team invitation ${invitationId}:`, error);
    throw error;
  }
};

/**
 * Get all pending team invitations for the current user
 */
export const getUserPendingTeamInvitations = async (): Promise<
  TeamInvitation[]
> => {
  try {
    const { data, error } = await supabase
      .rpc("get_user_pending_team_invitations");

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error("Error fetching user team invitations:", error);
    throw error;
  }
};

/**
 * Get team invitations for a specific team
 */
export const getTeamInvitations = async (
  teamId: string
): Promise<TeamInvitation[]> => {
  try {
    const { data, error } = await supabase
      .from("base_group_invitations")
      .select("*")
      .eq("group_id", teamId);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error(`Error fetching invitations for team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Remove a user from a team
 */
export const removeFromTeam = async (
  teamId: string,
  userId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("base_group_members")
      .delete()
      .eq("group_id", teamId)
      .eq("user_id", userId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Error removing user from team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Update team details
 */
export const updateTeam = async (
  teamId: string,
  updates: Partial<Pick<Team, "name" | "description">>
): Promise<Team | null> => {
  try {
    console.log(`[TeamService] 🔄 Updating team: ${teamId}`);

    // Use RPC function to bypass RLS restrictions
    const { data, error } = await supabase
      .rpc("update_team", {
        p_team_id: teamId,
        p_name: updates.name || "",
        p_description: updates.description || null,
      });

    if (error) {
      console.error(`[TeamService] ❌ RPC update failed:`, error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.error(`[TeamService] ❌ No data returned from update`);
      throw new Error("Team not found or update failed");
    }

    const teamData = data[0]; // RPC returns array
    console.log(`[TeamService] ✅ Team updated successfully: ${teamData.name}`);

    return teamData;
  } catch (error) {
    console.error(`Error updating team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Delete a team
 */
export const deleteTeam = async (teamId: string): Promise<boolean> => {
  try {
    console.log(`[TeamService] 🗑️ Deleting team: ${teamId}`);

    // Use RPC function to bypass RLS restrictions
    const { data, error } = await supabase
      .rpc("delete_team", {
        p_team_id: teamId,
      });

    if (error) {
      console.error(`[TeamService] ❌ RPC delete failed:`, error);
      throw error;
    }

    console.log(`[TeamService] ✅ Team deletion result: ${data}`);
    return data === true;
  } catch (error) {
    console.error(`Error deleting team ${teamId}:`, error);
    throw error;
  }
};

/**
 * Reject a team invitation
 */
export const rejectTeamInvitation = async (
  invitationId: string
): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from("base_group_invitations")
      .update({ status: "rejected", updated_at: new Date().toISOString() })
      .eq("id", invitationId);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error(`Error rejecting team invitation ${invitationId}:`, error);
    throw error;
  }
};

/**
 * Get teams that belong to a specific organization
 */
export const getOrganizationGroups = async (
  organizationId: string
): Promise<Team[]> => {
  try {
    console.log(`[TeamService] Fetching teams for organization: ${organizationId}`);

    // Try to use RPC function first (more reliable with RLS)
    try {
      const { data: rpcData, error: rpcError } = await supabase
        .rpc("get_user_teams");

      if (!rpcError && rpcData) {
        // Filter teams by organization ID from the RPC results
        const orgTeams = rpcData.filter((team: any) =>
          team.organisation_id === organizationId ||
          team.organization_id === organizationId
        );

        console.log(`[TeamService] ✅ Found ${orgTeams.length} teams via RPC for organization ${organizationId}`);
        return orgTeams.map((team: any) => ({
          id: team.id,
          name: team.name,
          description: team.description,
          created_by: team.created_by,
          created_at: team.created_at,
          updated_at: team.updated_at,
          organisation_id: organizationId
        }));
      }
    } catch (rpcError) {
      console.warn(`[TeamService] RPC failed, trying direct access:`, rpcError);
    }

    // Fallback to direct table access (might fail due to RLS)
    console.log(`[TeamService] ⚠️ Falling back to direct table access for organization ${organizationId}`);
    const { data, error } = await supabase
      .from("base_groups")
      .select("*")
      .eq("organisation_id", organizationId);

    if (error) {
      console.error(`[TeamService] ❌ Direct access failed:`, error);
      // Return empty array instead of throwing to prevent dialog from failing
      return [];
    }

    console.log(`[TeamService] ✅ Found ${data?.length || 0} teams via direct access`);
    return data || [];
  } catch (error) {
    console.error(
      `Error fetching teams for organization ${organizationId}:`,
      error
    );
    // Return empty array instead of throwing to prevent dialog from failing
    return [];
  }
};
